import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import JobCard from "./JobCard";
import {
  Briefcase,
  Filter,
  Search,
  SortAsc,
  Grid3x3,
  List,
  X,
  Sparkles,
} from "lucide-react";

const JobList = ({ jobs, onUpdateJob, applicationStatuses, statusFilter }) => {
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [sortBy, setSortBy] = useState("dateAdded"); // "dateAdded", "company", "status", "deadline"
  const [searchTerm, setSearchTerm] = useState("");

  // Filter jobs by status if filter is provided
  const filteredJobs = statusFilter
    ? jobs.filter((job) => job.applicationStatus === statusFilter)
    : jobs;

  // Filter by search term
  const searchedJobs = searchTerm
    ? filteredJobs.filter(
        (job) =>
          job.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (job.location &&
            job.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (job.workType &&
            job.workType.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (job.salaryRange &&
            job.salaryRange.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (job.skillsRequired &&
            job.skillsRequired.some((skill) =>
              skill.toLowerCase().includes(searchTerm.toLowerCase()),
            )) ||
          (job.notes &&
            job.notes.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    : filteredJobs;

  // Sort jobs
  const sortedJobs = [...searchedJobs].sort((a, b) => {
    switch (sortBy) {
      case "company":
        return a.companyName.localeCompare(b.companyName);
      case "status":
        return a.applicationStatus.localeCompare(b.applicationStatus);
      case "deadline":
        if (!a.applicationDeadline && !b.applicationDeadline) return 0;
        if (!a.applicationDeadline) return 1;
        if (!b.applicationDeadline) return -1;
        return (
          new Date(a.applicationDeadline) - new Date(b.applicationDeadline)
        );
      default: // dateAdded
        return new Date(b.dateAdded) - new Date(a.dateAdded);
    }
  });

  const toggleExpanded = (jobId) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  const getStatusStats = () => {
    const stats = {};
    filteredJobs.forEach((job) => {
      stats[job.applicationStatus] = (stats[job.applicationStatus] || 0) + 1;
    });
    return stats;
  };

  const statusStats = getStatusStats();

  if (sortedJobs.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="backdrop-blur-xl bg-light-card/80 border border-white/20 rounded-3xl p-12 max-w-md text-center shadow-2xl"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="w-20 h-20 text-primary-400 mx-auto mb-6"
          >
            <Briefcase className="w-20 h-20" />
          </motion.div>

          <h2 className="text-3xl font-black font-display text-gray-800 mb-4">
            {statusFilter ? `No ${statusFilter} Jobs` : "No Jobs Yet"}
          </h2>

          <p className="text-gray-600 mb-8 text-lg">
            {statusFilter
              ? `Try removing the filter or add jobs with this status`
              : "Start your journey by adding your first job application"}
          </p>

          <div className="flex flex-col gap-4">
            {statusFilter && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.reload()} // This would clear the filter
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl px-8 py-4 font-bold font-display transition-all duration-300 shadow-lg hover:shadow-gray-500/50 flex items-center gap-3"
              >
                <Filter className="w-5 h-5" />
                Clear Filter
              </motion.button>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="bg-gradient-to-r from-primary-500 to-accent-hot text-white rounded-2xl px-8 py-4 font-bold font-display transition-all duration-300 shadow-lg hover:shadow-primary-500/50 flex items-center gap-3"
            >
              <Sparkles className="w-5 h-5" />
              Add Your First Job
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="backdrop-blur-xl bg-light-card/80 border border-white/20 rounded-3xl p-6 shadow-2xl"
      >
        <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
          {/* Left: Search and Status */}
          <div className="flex-1 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search jobs by title, company, location, skills, or notes..."
                className="w-full pl-12 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/30 rounded-2xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300 font-display"
              />
            </div>

            {/* Status Filter Pills */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.reload()} // Clear filter
                className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 ${
                  !statusFilter
                    ? "bg-gradient-to-r from-primary-500 to-accent-hot text-white shadow-lg"
                    : "bg-white/60 backdrop-blur text-gray-700 hover:bg-white/80"
                }`}
              >
                All ({filteredJobs.length})
              </motion.button>

              {Object.entries(statusStats).map(([status, count]) => (
                <motion.button
                  key={status}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.hash = `status=${status}`)} // This would need to be handled by parent
                  className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 ${
                    statusFilter === status
                      ? "bg-gradient-to-r from-primary-500 to-accent-hot text-white shadow-lg"
                      : "bg-white/60 backdrop-blur text-gray-700 hover:bg-white/80"
                  }`}
                >
                  {status} ({count})
                </motion.button>
              ))}
            </div>
          </div>

          {/* Right: Sort and View Mode */}
          <div className="flex items-center gap-4">
            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white/60 backdrop-blur-sm border border-white/30 rounded-2xl px-4 py-3 pr-10 font-display text-gray-700 focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all duration-300 cursor-pointer"
              >
                <option value="dateAdded">Date Added</option>
                <option value="company">Company</option>
                <option value="status">Status</option>
                <option value="deadline">Deadline</option>
              </select>
              <SortAsc className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-white/60 backdrop-blur-sm border border-white/30 rounded-2xl p-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-primary-500 text-white"
                    : "text-gray-600 hover:bg-white/80"
                }`}
              >
                <Grid3x3 className="w-5 h-5" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-xl transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-primary-500 text-white"
                    : "text-gray-600 hover:bg-white/80"
                }`}
              >
                <List className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Active Filter Indicator */}
        {statusFilter && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 flex items-center justify-between bg-accent-electric/20 border border-accent-electric/30 rounded-2xl p-4"
          >
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-accent-electric" />
              <span className="font-bold font-display text-accent-electric">
                Filtered by: {statusFilter}
              </span>
              <span className="text-accent-electric/70">
                ({sortedJobs.length} jobs)
              </span>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()} // Clear filter
              className="px-4 py-2 bg-accent-electric text-white rounded-xl font-bold transition-all duration-200 hover:bg-accent-electric/80 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Clear
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      {/* Jobs Display */}
      <div
        className={
          viewMode === "grid"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-4"
        }
      >
        <AnimatePresence>
          {sortedJobs.map((job, index) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              layout
            >
              <JobCard
                job={job}
                isExpanded={expandedJobs.has(job.id)}
                onToggleExpanded={() => toggleExpanded(job.id)}
                onUpdateJob={onUpdateJob}
                applicationStatuses={applicationStatuses}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Floating Stats */}
      {sortedJobs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-8 left-8 z-40 backdrop-blur-xl bg-light-card/80 border border-white/20 rounded-2xl p-4 shadow-2xl"
        >
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                Total
              </p>
              <p className="text-2xl font-black font-display font-mono text-primary-600">
                {sortedJobs.length}
              </p>
            </div>

            {statusFilter && (
              <div className="text-center">
                <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                  Filtered
                </p>
                <p className="text-2xl font-black font-display font-mono text-accent-electric">
                  {sortedJobs.length}
                </p>
              </div>
            )}

            <div className="text-center">
              <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                This Week
              </p>
              <p className="text-2xl font-black font-display font-mono text-accent-neon">
                {
                  sortedJobs.filter((job) => {
                    const jobDate = new Date(job.dateAdded);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return jobDate >= weekAgo;
                  }).length
                }
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default JobList;
